/**
 * Forgot Password Page Component
 * Password reset request interface with email validation
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import config from '../../config/environment';
import '../Login/Login.css';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { showError, showSuccess } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      showError('Missing Email', 'Please enter your email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${config.api.baseUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        showSuccess('Email Sent', 'Password reset email sent! Check your inbox.');
      } else {
        showError('Failed', data.message || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      showError('Network Error', 'Please try again later');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Forgot Password?</h1>
          <p>We'll send you a link to reset your password</p>
        </div>

        {isSuccess ? (
          <div className="login-form">
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✉️</div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                Check Your Email
              </h3>
              <p style={{ color: '#718096', fontSize: '14px', marginBottom: '24px' }}>
                If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Link to="/login" className="login-button" style={{ textDecoration: 'none' }}>
                  Back to Login
                </Link>
                
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setEmail('');
                  }}
                  style={{
                    background: 'white',
                    color: '#667eea',
                    border: '2px solid #667eea',
                    padding: '12px 20px',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Send Another Email
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <p style={{ color: '#718096', fontSize: '13px' }}>
                Enter your email address and we'll send you a link to reset your password
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={isSubmitting}
                autoComplete="email"
                required
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-small"></span>
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        )}

        <div className="login-links">
          <Link to="/login" className="forgot-password-link">
            ← Back to Login
          </Link>
        </div>

        <div className="signup-prompt">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="signup-link">
              Sign up here
            </Link>
          </p>
        </div>

        <div className="login-footer">
          <p>&copy; 2024 Kadi Ni Tidi Platform. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
